import { defineStore } from "pinia"
import { postApiJson, fetchApiCatch } from "@/services/repowerAPIService.js"
import { setPlausibleUserSegment } from "@/services/plausibleService.js"
import HTTP_STATUS from "@/helpers/httpStatusHelper"
import useSessionStore from "@/stores/sessionStore.js"
import useChangelogStore from "@/stores/changelogStore.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import useFilterStore from "@/stores/filterStore.js"
import useInvoicesStore from "@/stores/repower/invoicesStore"
import useDashboardStore from "@/stores/dashboardStore.js"

export default defineStore("userStore", {
  state: () => ({
    fullName: "",
    email: "",
    flags: [],
    analyticsSegment: null,
  }),
  actions: {
    setUser(fullName, email, flags, analyticsSegment) {
      this.fullName = fullName ?? ""
      this.email = email ?? ""
      this.flags = flags ?? []
      this.analyticsSegment = analyticsSegment ?? null
    },
    loadMeteringPoints(onLoadFail) {
      const meteringPointsStore = useMeteringPointsStore()
      const assetsPromise = meteringPointsStore.loadAssets(null, onLoadFail)
      meteringPointsStore.loadAllocationPoints(null, onLoadFail)
      meteringPointsStore.loadPortfolios(() => {
        const defaultPortfolio = meteringPointsStore.portfolios[0]
        if (defaultPortfolio) {
          useFilterStore().setPortfolio(defaultPortfolio)
          useInvoicesStore().loadInvoiceSettings(defaultPortfolio.id)
          // Delay loading portfolio assets to until after assets are loaded
          assetsPromise.then(() => {
            useFilterStore().currentPortfolio.loadPortfolioAssets(
              null,
              onLoadFail,
            )
          })
        }
      }, onLoadFail)
    },
    login(email, password, onSuccess, onUnknown, onFail, onLoadFail) {
      // Attempt login
      postApiJson("/users/login/", { email, password })
        .then((data) => {
          this.setUser(
            data.user_full_name,
            data.user_email,
            data.user_flags,
            data.analytics_segment,
          )
          setPlausibleUserSegment(data.analytics_segment)
          useSessionStore().setAuthenticated(true)
          useChangelogStore().loadDateUpdated()
          this.loadMeteringPoints(onLoadFail)
          onSuccess?.()
        })
        .catch((error) => {
          if (error.code === HTTP_STATUS.BAD_REQUEST) {
            onUnknown?.(error)
          } else {
            onFail?.(error)
          }
        })
    },
    logout(onSuccess, onFail) {
      const meteringPointsStore = useMeteringPointsStore()
      fetchApiCatch("/users/logout/")
        .then(() => {
          // Reset session
          const sessionStore = useSessionStore()
          sessionStore.setAuthenticated(false)
          sessionStore.requestCsrfToken()
          // Reset other stores
          useChangelogStore().$reset()
          meteringPointsStore.$reset()
          meteringPointsStore.$reset()
          useFilterStore().$reset()
          useDashboardStore().$reset()
          this.$reset()
          // Reset custom properties
          setPlausibleUserSegment("unauthenticated")
          // Run callback
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeFullName(fullName, onSuccess, onFail) {
      postApiJson(
        "/users/change-full-name/",
        { full_name: fullName },
        { method: "PUT" },
      )
        .then(() => {
          this.fullName = fullName
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    requestEmailChange(newEmail, onSuccess, onFail) {
      postApiJson(
        "/users/request-email-change/",
        { new_email: newEmail },
        { method: "POST" },
      )
        .then(() => {
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeEmail(newEmail, token, password, onSuccess, onFail) {
      postApiJson(
        "/users/change-email/",
        { token, password },
        { method: "PUT" },
      )
        .then(() => {
          this.email = newEmail
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changePassword(currentPassword, newPassword, onSuccess, onFail) {
      postApiJson(
        "/users/change-password/",
        { current_password: currentPassword, new_password: newPassword },
        { method: "PUT" },
      )
        .then(() => {
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    hasFlag(flag) {
      return this.flags.includes(flag)
    },
  },
  persist: true,
})
